/**
 * PerformanceMonitor.ts
 *
 * Lightweight, zero-dependency performance profiler for the engine layer.
 * Active only when explicitly enabled (typically in dev/debug builds).
 * Does NOT import Phaser — safe for use anywhere in src/engine/.
 *
 * Usage:
 *   PerformanceMonitor.enable()
 *   PerformanceMonitor.begin('matchEngine.update')
 *   // ... work ...
 *   PerformanceMonitor.end('matchEngine.update')
 *   console.table(PerformanceMonitor.getReport())
 */
export class PerformanceMonitor {
  private static _enabled = false
  private static _marks: Map<string, number> = new Map()
  private static _samples: Map<string, number[]> = new Map()

  /**
   * Warn when a single measurement exceeds this threshold (ms).
   * ~16ms = 60 fps budget for one full frame.
   */
  private static readonly WARN_THRESHOLD_MS = 16

  /** Maximum rolling-window size per label. */
  private static readonly MAX_SAMPLES = 120

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  static enable(): void {
    this._enabled = true
  }

  static disable(): void {
    this._enabled = false
  }

  static isEnabled(): boolean {
    return this._enabled
  }

  static reset(): void {
    this._marks.clear()
    this._samples.clear()
  }

  // ---------------------------------------------------------------------------
  // Measurement
  // ---------------------------------------------------------------------------

  /**
   * Start a measurement. Call `end(label)` with the same label to record it.
   * No-op when disabled.
   */
  static begin(label: string): void {
    if (!this._enabled) return
    this._marks.set(label, performance.now())
  }

  /**
   * End a measurement started with `begin(label)`.
   * Logs a console warning when the elapsed time exceeds WARN_THRESHOLD_MS.
   * No-op when disabled or if `begin` was never called for this label.
   */
  static end(label: string): void {
    if (!this._enabled) return

    const start = this._marks.get(label)
    if (start === undefined) return

    const duration = performance.now() - start
    this._marks.delete(label)

    let samples = this._samples.get(label)
    if (!samples) {
      samples = []
      this._samples.set(label, samples)
    }
    samples.push(duration)
    if (samples.length > this.MAX_SAMPLES) {
      samples.shift()
    }

    if (duration > this.WARN_THRESHOLD_MS) {
      const avg = samples.reduce((a, b) => a + b, 0) / samples.length
      console.warn(
        `[Perf] ${label}: ${duration.toFixed(1)}ms (avg over ${samples.length} samples: ${avg.toFixed(1)}ms)`,
      )
    }
  }

  // ---------------------------------------------------------------------------
  // Reporting
  // ---------------------------------------------------------------------------

  /**
   * Returns a snapshot of all recorded labels with avg/min/max/p95 statistics.
   * Suitable for `console.table()`.
   */
  static getReport(): Record<
    string,
    { avg_ms: number; min_ms: number; max_ms: number; p95_ms: number; samples: number }
  > {
    const report: Record<
      string,
      { avg_ms: number; min_ms: number; max_ms: number; p95_ms: number; samples: number }
    > = {}

    for (const [label, samples] of this._samples) {
      if (samples.length === 0) continue

      const sorted = [...samples].sort((a, b) => a - b)
      const avg = samples.reduce((a, b) => a + b, 0) / samples.length
      const p95Idx = Math.floor(sorted.length * 0.95)

      report[label] = {
        avg_ms: Number(avg.toFixed(2)),
        min_ms: Number(sorted[0].toFixed(2)),
        max_ms: Number(sorted[sorted.length - 1].toFixed(2)),
        p95_ms: Number((sorted[p95Idx] ?? sorted[sorted.length - 1]).toFixed(2)),
        samples: samples.length,
      }
    }

    return report
  }
}
